import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { PhoneNav } from "../../../shared/phone/PhoneNav";
import { PnRow, Segmented } from "../../../shared/phone/Row";
import { ActionSheet, Sheet } from "../../../shared/phone/Sheet";
import { usePhone } from "../../../shared/phone/media";
import { refreshBadges } from "../../components/phone/badges";
import { Icon } from "../../components/ui/Icon";
import { ApiError } from "../../lib/api";
import { useOnShown } from "../../lib/inApp";
import { lessonsApi, type Lesson } from "../../lib/lessonsApi";
import { libraryApi, type LibraryEntry } from "../../lib/libraryApi";
import { useToast } from "../goal/useToast";
import { cap, shortDate } from "../products/text";
import { evidenceText, lessonStr, sentence, skillEntry, strengthWords } from "./lessonText";
import { reloadShelf } from "./useShelf";
import { useCan } from "../../context/AuthContext";

const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");

type Call = "agree" | "dispute";

const CALL_NOTE: Record<Call, string> = {
  agree: "You agreed. New results still re-check it.",
  dispute: "You disputed it. Plans now treat it as a guess and re-test it before leaning on it.",
};

const HOW: [string, string][] = [
  ["Only from evidence", "A test your rules allowed it to call, a pattern across enough results, or something you told it."],
  ["Proposed, then yours to accept", "New lessons arrive in the morning review. Only accepted ones shape plans and skills."],
  ["Guesses get re-tested", "Medium and weak lessons, or ones from another place, are re-tested before a plan leans on them."],
  ["Better skills, with your approval", "When lessons point to a better way, the agent proposes a skill change. You approve it for one goal or your whole library."],
];

const PILL: Record<string, [string, string]> = {
  strong: ["good", "Strong"],
  medium: ["acc", "Medium"],
  weak: ["", "Weak"],
  disputed: ["violet", "Disputed"],
  retired: ["", "Retired"],
};

/** How sure a lesson is, as a pill: its strength, Disputed once the owner disputes it, or Retired. */
function Strength({ of }: { of: string }) {
  const [tone, word] = PILL[of] ?? ["", cap(of)];
  return <span className={tone ? `pn-pill ${tone}` : "pn-pill"}>{word}</span>;
}

interface Loaded {
  lessons: Lesson[];
  skills: LibraryEntry[];
}

/**
 * Lessons on a phone or an iPad: new lessons wait first with Reject and Accept, then the skill changes they
 * suggest, then every accepted lesson as a row with how sure it is. A lesson opens in a sheet.
 */
export function LessonsNative() {
  const phone = usePhone();
  const [data, setData] = useState<Loaded | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState<string | null>(null);
  const [how, setHow] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const may = useCan()("editor");
  // Why: an applied change is confirmed where it was applied, not on every visit after.
  const [applied, setApplied] = useState<string[]>([]);
  const { toast, say } = useToast();

  const load = useCallback(async (): Promise<Loaded> => {
    const [l, lib] = await Promise.all([lessonsApi.list(), libraryApi.list()]);
    const next = { lessons: l.lessons, skills: lib.items.filter((e) => e.kind === "skill") };
    setData(next);
    return next;
  }, []);
  useEffect(() => {
    load().catch((e: unknown) => setErr(message(e)));
  }, [load]);
  // Why quiet: the list keeps what it shows; a failed read leaves it as it is.
  useOnShown(() => void load().catch(() => {}));

  /** Run one decision, then show the lessons as they are now. A decision made elsewhere first (409) reloads and says so. */
  const act = async (id: string, run: () => Promise<unknown>, done?: (fresh: Loaded) => string, counts = false) => {
    setBusy(id);
    try {
      await run();
      const fresh = await load();
      if (done) say(done(fresh));
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) await load().catch(() => undefined);
      say(message(e), false);
    } finally {
      setBusy(null);
      if (counts) refreshBadges();
      void reloadShelf();
    }
  };
  const decide = (l: Lesson, d: "accept" | "reject") =>
    act(l.id, () => lessonsApi.decide(l.id, d), () => (d === "accept" ? "Lesson accepted. It can now shape plans and skills." : "Rejected. Kept in history, never used."), true);
  const applyChange = (l: Lesson, scope: "library" | "goal") =>
    act(
      l.id,
      async () => {
        await lessonsApi.applyChange(l.id, scope);
        setApplied((a) => [...a, l.id]);
      },
      (fresh) => {
        const title = skillEntry(fresh.skills, l.skillChange!.skill)?.latest.title ?? l.skillChange!.skill;
        if (scope === "goal") return `${title} is changed for that goal only.`;
        const sc = fresh.lessons.find((x) => x.id === l.id)?.skillChange;
        const made = fresh.skills.find((e) => e.latest.id === sc?.appliedTo);
        return made ? `${title} is now v${made.latest.version} in your library.` : `${title} is changed in your library.`;
      },
    );

  const lessons = data?.lessons ?? [];
  const skills = data?.skills ?? [];
  const skillTitle = (name: string) => skillEntry(skills, name)?.latest.title ?? name;
  const opts = ["all", "strong", "medium", "weak", ...(lessons.some((l) => lessonStr(l) === "disputed") ? ["disputed"] : [])];
  const f = opts.includes(filter) ? filter : "all";
  const ok = (s: string) => f === "all" || s === f;
  const waiting = lessons.filter((l) => l.state === "proposed" && ok(l.strength));
  const changes = lessons.filter(
    (l) =>
      l.state === "accepted" &&
      l.skillChange &&
      ok(lessonStr(l)) &&
      (l.skillChange.status === "proposed" || (l.skillChange.status === "applied" && applied.includes(l.id))),
  );
  const kept = lessons.filter((l) => (l.state === "accepted" || l.state === "retired") && ok(lessonStr(l))).toReversed();
  const rejected = lessons.filter((l) => l.state === "rejected").length;
  const inSheet = lessons.find((l) => l.id === open) ?? null;
  const none = data !== null && lessons.every((l) => l.state === "rejected");

  return (
    <div className="pn-screen">
      <PhoneNav
        title="Lessons"
        back={phone ? { to: "/library", label: "Library" } : undefined}
        large
        actions={
          <button type="button" className="pn-tbtn" aria-label="How lessons work" onClick={() => setHow(true)}>
            <Icon name="lbQuestion" />
          </button>
        }
      />
      <div className="pn-stack">
        {err ? (
          <div className="pn-empty">
            <b>The lessons did not load</b>
            {err}
          </div>
        ) : null}
        {none ? (
          <div className="pn-empty">
            <b>No lessons yet</b>
            Anything a goal learns waits here for your OK.
          </div>
        ) : null}
        {data && !none ? (
          <div className={opts.length > 4 ? "lb-fil five" : "lb-fil"}>
            <Segmented label="How sure" value={f} onChange={setFilter} options={opts.map((k) => ({ value: k, label: PILL[k]?.[1] ?? "All" }))} />
          </div>
        ) : null}
        {waiting.length + changes.length > 0 ? (
          <section>
            <div className="pn-cap">Waits for you</div>
            <div className="lb-cards">
              {waiting.map((l) => (
                <article className="pn-card lb-lw" key={l.id} aria-label={`New lesson ${l.code}`}>
                  <div className="lb-lw-h">
                    <span className="lb-lw-k">
                      <Icon name="lbSparkle" />
                      New lesson · {shortDate(l.createdAt)}
                    </span>
                    <Strength of={l.strength} />
                  </div>
                  <button type="button" className="lb-lw-t" onClick={() => setOpen(l.id)}>
                    {l.claim}
                  </button>
                  {l.evidence.length > 0 ? <p className="lb-lw-ev">{evidenceText(l)}</p> : null}
                  {l.learnedIn?.sentence ? (
                    <p className="lb-lw-f">
                      <Icon name="target" />
                      <span>{l.learnedIn.sentence}</span>
                    </p>
                  ) : null}
                  {may && (
                    <div className="pn-btns">
                      <button type="button" className="pn-btn sm" disabled={busy === l.id} onClick={() => void decide(l, "reject")}>
                        Reject
                      </button>
                      <button type="button" className="pn-btn sm pri" disabled={busy === l.id} onClick={() => void decide(l, "accept")}>
                        Accept
                      </button>
                    </div>
                  )}
                </article>
              ))}
              {changes.map((l) => {
                const sc = l.skillChange!;
                const title = skillTitle(sc.skill);
                const entry = skillEntry(skills, sc.skill);
                if (sc.status === "applied") {
                  const made = skills.find((e) => e.latest.id === sc.appliedTo);
                  return (
                    <article className="pn-card lb-done" key={l.id} aria-label={`Skill change from ${l.code}`}>
                      <div className="lb-done-h">
                        <span className="pn-ico good">
                          <Icon name="check" />
                        </span>
                        <p>
                          {sc.appliedAs === "goal" ? (
                            <>
                              <b>
                                {title} is changed for the goal “{l.learnedIn?.sentence ?? "it came from"}” only.
                              </b>{" "}
                              {entry ? `Your library keeps v${entry.latest.version}.` : "Your library keeps its version."}
                            </>
                          ) : (
                            <>
                              <b>
                                {title} is now {made ? `v${made.latest.version}` : "changed"} in your library.
                              </b>{" "}
                              New goals start with it. Running goals keep the version they have.
                            </>
                          )}
                        </p>
                      </div>
                      {sc.appliedAs !== "goal" && sc.appliedTo ? (
                        <Link className="lb-upd-l" to={`/library/${encodeURIComponent(sc.appliedTo)}`}>
                          <span>{title}</span>
                          <Icon name="right" />
                        </Link>
                      ) : null}
                    </article>
                  );
                }
                return (
                  <article className="pn-card lb-lw" key={l.id} aria-label={`Skill change from ${l.code}`}>
                    <div className="lb-lw-h">
                      <span className="lb-lw-k">
                        <Icon name="lbSparkle" />
                        Suggested skill change
                      </span>
                      <span className="lb-lw-code">From lesson {l.code}</span>
                    </div>
                    <button type="button" className="lb-lw-t" onClick={() => setOpen(l.id)}>
                      {title}: {sc.change}
                    </button>
                    {sc.canApply ? (
                      <div className="lb-diff">
                        <p className="lb-dl rm">
                          <i aria-hidden="true">-</i>
                          <span>{sc.before}</span>
                        </p>
                        <p className="lb-dl add">
                          <i aria-hidden="true">+</i>
                          <span>{sc.after}</span>
                        </p>
                      </div>
                    ) : (
                      <p className="lb-lw-note">This change has no exact words to apply. Open the skill and make the change there.</p>
                    )}
                    <p className="lb-lw-why">
                      <b>Why:</b> {l.code} is {strengthWords(l)}
                      {l.whyStrength ? `: ${l.whyStrength}` : "."}
                    </p>
                    {sc.canApply && may ? (
                      <>
                        <button type="button" className="pn-btn sm pri wide" disabled={busy === l.id} onClick={() => void applyChange(l, "library")}>
                          Approve for my library
                        </button>
                        <div className="pn-btns">
                          {l.learnedIn ? (
                            <button type="button" className="pn-btn sm" disabled={busy === l.id} onClick={() => void applyChange(l, "goal")}>
                              This goal only
                            </button>
                          ) : null}
                          <button type="button" className="pn-btn sm ghost" disabled={busy === l.id} onClick={() => void act(l.id, () => lessonsApi.declineChange(l.id), () => "Skill change declined. The lesson stays.")}>
                            Not now
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="pn-btns">
                        {may && (
                          <button type="button" className="pn-btn sm ghost" disabled={busy === l.id} onClick={() => void act(l.id, () => lessonsApi.declineChange(l.id), () => "Skill change declined. The lesson stays.")}>
                            Not now
                          </button>
                        )}
                        {entry ? (
                          <Link className="pn-btn sm soft" to={`/library/${encodeURIComponent(entry.latest.id)}`}>
                            Open {title}
                          </Link>
                        ) : null}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </section>
        ) : null}
        {data && !none ? (
          <section>
            <div className="pn-cap">Accepted</div>
            <div className="pn-grp">
              {kept.map((l) => (
                <PnRow
                  key={l.id}
                  onClick={() => setOpen(l.id)}
                  title={l.claim}
                  sub={l.applies ?? undefined}
                  value={<Strength of={lessonStr(l)} />}
                  wrap
                  className={l.state === "retired" ? "lb-lr off" : "lb-lr"}
                />
              ))}
              {kept.length === 0 ? <PnRow title="No lessons here yet" className="lb-dim" /> : null}
            </div>
            {rejected > 0 ? (
              <p className="pn-note">
                {rejected} rejected {rejected === 1 ? "lesson" : "lessons"} kept in history, never used.
              </p>
            ) : null}
          </section>
        ) : null}
      </div>
      <LessonSheet
        lesson={inSheet}
        busy={busy !== null && busy === open}
        onClose={() => setOpen(null)}
        onDecide={(l, d) => {
          setOpen(null);
          void decide(l, d);
        }}
        onCall={(l, c) => void act(l.id, () => lessonsApi.call(l.id, c), c === "retire" ? () => "Retired. Plans no longer use it." : undefined)}
      />
      <Sheet
        open={how}
        onClose={() => setHow(false)}
        title="How lessons work"
        right={
          <button type="button" className="pn-tbtn strong" onClick={() => setHow(false)}>
            Done
          </button>
        }
      >
        <div className="pn-grp">
          {HOW.map(([b, p], i) => (
            <div className="pn-row static lb-how" key={b}>
              <span className="lb-hn">{i + 1}</span>
              <span className="m">
                <span className="t">{b}</span>
                <span className="s">{p}</span>
              </span>
            </div>
          ))}
        </div>
      </Sheet>
      {toast}
    </div>
  );
}

/** One lesson in a sheet: what it is based on, how sure, where it applies and came from, what uses it, and the owner's call. */
function LessonSheet({
  lesson: l,
  busy,
  onClose,
  onDecide,
  onCall,
}: {
  lesson: Lesson | null;
  busy: boolean;
  onClose: () => void;
  onDecide: (l: Lesson, d: "accept" | "reject") => void;
  onCall: (l: Lesson, c: Call | "retire") => void;
}) {
  const [ask, setAsk] = useState(false);
  const may = useCan()("editor");
  const from = l?.learnedIn;
  const log = from ? `/goals/${encodeURIComponent(from.goalId)}/log${from.runId ? `?run=${encodeURIComponent(from.runId)}` : ""}` : null;
  const waits = l?.state === "proposed";
  return (
    <>
      <Sheet
        open={l !== null}
        onClose={onClose}
        title={l ? `Lesson ${l.code}` : "Lesson"}
        right={
          <button type="button" className="pn-tbtn strong" onClick={onClose}>
            Done
          </button>
        }
        foot={
          l && waits && may ? (
            <div className="pn-btns">
              <button type="button" className="pn-btn" disabled={busy} onClick={() => onDecide(l, "reject")}>
                Reject
              </button>
              <button type="button" className="pn-btn pri" disabled={busy} onClick={() => onDecide(l, "accept")}>
                Accept
              </button>
            </div>
          ) : undefined
        }
      >
        {l ? (
          <div className="pn-stack tight lb-lsb">
            <div className="lb-ls">
              <div className="lb-ls-h">
                <Strength of={waits ? l.strength : lessonStr(l)} />
                {waits ? <span className="pn-pill warn">Waits for you</span> : null}
              </div>
              <h2>{l.claim}</h2>
            </div>
            <section>
              <div className="pn-cap">What it is based on</div>
              <div className="pn-grp">
                {l.evidence.length ? (
                  l.evidence.map((e, i) => <PnRow key={i} title={<span className="lb-ev">{sentence(e.fact)}</span>} sub={e.source} wrap />)
                ) : (
                  <PnRow title="Not said." className="lb-dim" />
                )}
              </div>
            </section>
            <section>
              <div className="pn-cap">How sure</div>
              <div className="pn-grp">
                <PnRow
                  title={
                    <span className="lb-ev">
                      <b>{cap(l.strength)}.</b> {l.whyStrength}
                    </span>
                  }
                  wrap
                />
              </div>
            </section>
            <section>
              <div className="pn-cap">Where it applies</div>
              <div className="pn-grp">
                <PnRow title={<span className="lb-ev">{l.applies ?? "Not said."}</span>} wrap />
              </div>
            </section>
            <section>
              <div className="pn-cap">Where it came from</div>
              <div className="pn-grp">
                {log ? (
                  <PnRow to={log} icon={<Icon name="lbList" />} iconTone="plain" title={from?.sentence ?? "Its goal"} sub={`${shortDate(l.createdAt)} · Open in the log`} wrap />
                ) : (
                  <PnRow icon={<Icon name="lbList" />} iconTone="plain" title="An earlier run" sub={shortDate(l.createdAt)} wrap />
                )}
              </div>
            </section>
            <section>
              <div className="pn-cap">Used by</div>
              <div className="pn-grp">
                {l.usedBy.length ? (
                  l.usedBy.map((u) => <PnRow key={u.goalId} icon={<Icon name="target" />} title={u.sentence} wrap />)
                ) : (
                  <PnRow title="Nothing yet" className="lb-dim" />
                )}
              </div>
            </section>
            {l.state === "accepted" ? (
              may && (
                <>
                  <section>
                    <div className="pn-cap">Your call</div>
                    <div className={busy ? "lb-call busy" : "lb-call"}>
                      <Segmented<Call | "">
                        label="Your call"
                        value={l.call ?? ""}
                        onChange={(c) => c && c !== l.call && onCall(l, c)}
                        options={[
                          { value: "agree", label: "I agree" },
                          { value: "dispute", label: "I disagree" },
                        ]}
                      />
                    </div>
                    {l.call ? <p className="pn-note">{CALL_NOTE[l.call]}</p> : null}
                  </section>
                  <div className="pn-grp">
                    <PnRow onClick={() => setAsk(true)} title="Retire it" danger />
                  </div>
                </>
              )
            ) : l.state === "retired" ? (
              <p className="pn-note">You retired it. Plans no longer use it. Its history stays in the log.</p>
            ) : null}
          </div>
        ) : (
          <span />
        )}
      </Sheet>
      <ActionSheet
        open={ask && l !== null}
        onClose={() => setAsk(false)}
        title="Retire this lesson?"
        message="Plans stop using it. Its history stays in the log."
        actions={[{ label: "Retire it", danger: true, onSelect: () => l && onCall(l, "retire") }]}
      />
    </>
  );
}
